// GalleryUIMobile.tsx

import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import LazyMedia from "./LazyMediaMobile";
import { useTranslation } from "react-i18next";
import Hls from "hls.js";
import { useHlsPrefetch } from "../../../shared/utils/useHlsPrefetch";
import FollowButtonInline from "../../components/FollowButtonMobile";
import Modal from "../../components/ModalReport";
import api from '../../../shared/api/axios';
    

const API_BASE = process.env.REACT_APP_API_BASE_URL as string;

if (!API_BASE) {
  throw new Error("REACT_APP_API_BASE_URL is not defined");
}

// normalize → remove trailing /api
const API_ORIGIN = API_BASE.replace(/\/api(\/)?$/, '').replace(/\/$/, '');

const isUuid = (v?: string | null) => !!v && /^[0-9a-f-]{36}$/i.test(v);

const resolveVideoSrc = (media: MediaEntry): string => {
  if (media.hlsUrl?.trim()) return media.hlsUrl;
  if (media.videoId && isUuid(media.videoId)) {
    return `${API_ORIGIN}/hls/playlist/${media.videoId}`;
  }
  if (media.url && /^https?:\/\//i.test(media.url) && /\.m3u8($|\?)/i.test(media.url)) {
    return media.url;
  }
  return "";
};

const resolvePrefetchVideoId = (media: MediaEntry): string | null => {
  if (media.videoId && isUuid(media.videoId)) return media.videoId;

  if (media.hlsUrl) {
    const match = media.hlsUrl.match(/\/hls\/playlist\/([0-9a-f-]{36})/i);
    if (match) return match[1];
  }

  if (media.url) {
    const match = media.url.match(/\/hls\/playlist\/([0-9a-f-]{36})/i);
    if (match) return match[1];
  }

  return null;
};

const isVideoEntry = (media: MediaEntry) => {
  return !!(
    media.isHls ||
    media.videoId ||
    media.hlsUrl ||
    media.dashUrl ||
    (media.url && /\.m3u8($|\?)/i.test(media.url))
  );
};

export const HLSPlayer = ({
  src,
  poster,
  muted,
  active,
  style = {},
  onOrientation,
}: {
  src: string;
  poster?: string;
  muted: boolean;
  active: boolean;
  style?: React.CSSProperties;
  onOrientation?: (isLandscape: boolean) => void;
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  const [showControls, setShowControls] = useState(true); // Controls are visible by default
  const [progress, setProgress] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(muted);
  const [userPaused, setUserPaused] = useState(false);

  const emitOrientation = (v: HTMLVideoElement) => {
    if (!v.videoWidth || !v.videoHeight) return;
    onOrientation?.(v.videoWidth > v.videoHeight);
  };

  // Play/pause icons
  const PlayIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="#bcb4b4">
      <path d="M7 5v14l11-7z" />
    </svg>
  );

  const PauseIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="#bcb4b4">
      <rect x="7.2" y="5" width="3.6" height="14" rx="1.2" />
      <rect x="13.2" y="5" width="3.6" height="14" rx="1.2" />
    </svg>
  );

  const SpeakerOnIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
      <path d="M15 9a4 4 0 0 1 0 6" />
      <path d="M18 7a7 7 0 0 1 0 10" />
    </svg>
  );

  const SpeakerOffIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
      <line x1="23" y1="9" x2="17" y2="15" />
      <line x1="17" y1="9" x2="23" y2="15" />
    </svg>
  );

  /* ---------------- HLS attach ---------------- */
  useEffect(() => {
  const video = videoRef.current;
  if (!video || !src) return;

  let hls: Hls | null = null;

  if (video.canPlayType("application/vnd.apple.mpegurl")) {
    video.src = src;
    video.load();
  } else if (Hls.isSupported()) {
    hls = new Hls({ startLevel: 0, enableWorker: true });
    hls.loadSource(src);
    hls.attachMedia(video);
  }

  return () => {
    try {
      if (hls) hls.destroy();
      video.pause();
      video.removeAttribute("src");
      video.load();
    } catch {}
  };
}, [src]);

  /* ---------------- autoplay logic ---------------- */
  useEffect(() => {
  const v = videoRef.current;
  if (!v) return;

  // 👇 if post is NOT active → force pause
  if (!active) {
    v.pause();
    return;
  }

  // 👇 if active and user didn't pause → play
  if (!userPaused) {
    v.play().catch(() => {});
  }
}, [active, userPaused]);

  /* ---------------- progress tracking ---------------- */
  const onTimeUpdate = () => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    setProgress((v.currentTime / v.duration) * 100);
  };

  const handleScrub = (value: number) => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    v.currentTime = (v.duration * value) / 100;
  };

  const formatTime = (sec: number) => {
    if (!sec || isNaN(sec)) return "0:00";
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  /* ---------------- volume ---------------- */
  const handleVolume = (value: number) => {
    const v = videoRef.current;
    if (!v) return;
    v.volume = value;
    setVolume(value);
  };

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = isMuted;
  }, [isMuted]);

  /* ---------------- play/pause ---------------- */
  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;

    if (!v.paused && !v.ended) {
      v.pause();
      setUserPaused(true);
    } else {
      v.play().catch(() => {});
      setUserPaused(false);
    }
  };

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "hidden",
      }}
      onClick={() => setShowControls((prev) => !prev)} // Toggle controls visibility
    >
      {/* VIDEO */}
      <video
        ref={videoRef}
        poster={poster}
        muted={isMuted}
        playsInline
        preload="auto"
        controls={false}
        onPointerDown={togglePlay}
        onLoadedMetadata={(e) => {
          emitOrientation(e.currentTarget);
        }}
        onLoadedData={(e) => emitOrientation(e.currentTarget)}
        onCanPlay={(e) => emitOrientation(e.currentTarget)}
        onTimeUpdate={onTimeUpdate}
        onPlay={() => {
          setIsPlaying(true);
          setUserPaused(false);
        }}
        onPause={() => {
          setIsPlaying(false);
        }}
        onEnded={() => {
          const v = videoRef.current;
          if (!v) return;

          v.currentTime = 0;
          v.play().catch(() => {});
        }}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
          display: "block",
          transform: "translateZ(0)",
          backfaceVisibility: "hidden",
          ...style,
        }}
      />

      {/* CONTROLS */}
      {showControls && (
        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            zIndex: 5,
            width: "100%",
            background: "rgba(0,0,0,0.65)",
            backdropFilter: "blur(6px)",
            opacity: 1,
            transition: "opacity 200ms ease",
            borderBottomLeftRadius: 12,
            borderBottomRightRadius: 12,
            overflow: "hidden",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              padding: "5px 8px",
            }}
          >
            {/* LEFT — PLAY */}
            <div style={{ display: "flex", alignItems: "center" }}>
              <button onPointerDown={togglePlay} style={controlBtn}>
                {isPlaying ? <PauseIcon /> : <PlayIcon />}
              </button>
            </div>

            {/* CENTER — MAIN SCRUBBER */}
            <div
              style={{
                flex: "1 1 auto",
                display: "flex",
                alignItems: "center",
                padding: "0 6px",
                maxWidth: "55%", // 🔥 limits the scrubber width
                minWidth: 120, // keeps it usable on small screens
              }}
            >
              <input
                type="range"
                min={0}
                max={100}
                value={progress}
                onChange={(e) => handleScrub(Number(e.target.value))}
                className="custom-scrubber"
                style={{
                  width: "100%",
                  height: 3,
                  background: `linear-gradient(
                    to right,
                    #d8cece 0%,
                    #d8cece ${progress}%,
                    #555 ${progress}%,
                    #555 100%
                  )`,
                }}
              />
            </div>

            {/* RIGHT — TIME + VOLUME */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                flexShrink: 0,
              }}
            >
              {/* TIME */}
              <span
                style={{
                  fontSize: 11,
                  color: "#bbb",
                  whiteSpace: "nowrap",
                  marginLeft: -2,
                  marginRight: 7,
                  fontVariantNumeric: "tabular-nums", // 👈 stable width numbers
                }}
              >
                {formatTime(videoRef.current?.currentTime || 0)}/
                {formatTime(videoRef.current?.duration || 0)}
              </span>

              {/* SPEAKER */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsMuted(!isMuted);
                }}
                style={controlBtnVolume}
              >
                {isMuted ? <SpeakerOffIcon /> : <SpeakerOnIcon />}
              </button>

              {/* VOLUME */}
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={isMuted ? 0 : volume}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  setIsMuted(val === 0);
                  handleVolume(val);
                }}
                className="volume-slider"
                style={{
                  width: 65,
                  height: 2,
                  background: `linear-gradient(
                    to right,
                    #bcb3b3 0%,
                    #d8cece ${(isMuted ? 0 : volume) * 100}%,
                    #666 ${(isMuted ? 0 : volume) * 100}%,
                    #666 100%
                  )`,
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const controlBtn: React.CSSProperties = {
  background: "transparent",
  border: "none",
  color: "#bcb4b4",
  borderRadius: 6,
  cursor: "pointer",
  fontSize: 20,

  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  height: 24, // 👈 locks height
  lineHeight: 1, // 👈 prevents font expansion
  padding: 0, // 👈 removes vertical shift
};

const controlBtnVolume: React.CSSProperties = {
  background: "transparent",
  border: "none",
  color: "#bcb4b4",
  borderRadius: 6,
  cursor: "pointer",
  fontSize: 20,

  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  height: 24,
  lineHeight: 1,
  padding: 0,
};



/** ——— Types (structural typing means these can mirror your Gallery.tsx types) ——— */
export interface MediaEntry {
  // image OR fallback url
  url?: string;

  // new video pipeline
  videoId?: string | null;
  isHls?: boolean;
  hlsUrl?: string | null;
  dashUrl?: string | null;

  // optional poster from backend
  posterUrl?: string | null;

  musicId?: string | null;
  musicTitle?: string | null;
  musicArtist?: string | null;
}


export interface Post {
  _id: string;
  media: MediaEntry[];
  captions: string[];
  createdAt: string;
  likes: number;
  likedByUser?: boolean;
  location?: {
    city: string;
    country: string;
    countryCode?: string;
  };
  user: {
    _id: string;
    profilename: string;
    picture?: string | null;
  } | null;
  totalLikes?: number;
  likeKey?: string;
  hashtags?: string[];
  status?: string;

  // 👇 NEW: ad-specific fields (from /media backend)
  isAd?: boolean;          // true for ads
  adLabel?: string;        // e.g. "Sponsored"
  adCtaText?: string;      // CTA button text
  adUrl?: string;          // click URL
  advertiserName?: string; // optional nice name
}


/** ——— Local helpers/consts used by the UI components ——— */

// Media sizing
const MUSIC_BAR_HEIGHT = 38; // px reserved
const MEDIA_BOX = `calc(100dvh - 108px - ${MUSIC_BAR_HEIGHT}px)`;
const MEDIA_HEIGHT = MEDIA_BOX;

// Styles (duplicated locally so Gallery.tsx doesn't need changes)
const postCardStyle: React.CSSProperties = {
  background:
    'linear-gradient(145deg, rgba(6, 11, 23, 0.95), rgba(4, 7, 15, 0.95))',
  padding: 0,               // ✅ remove side padding
  borderRadius: 0,          // ✅ remove rounded frame
  marginBottom: 0,          // ✅ remove gap between posts
  boxShadow: "none",        // ✅ remove card shadow
  color: "#888",
};
const mediaScrollContainerStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "row",
  overflowX: "auto",
  overflowY: "hidden",
  height: MEDIA_HEIGHT,   // ✅ lock height
  alignItems: "center",
  gap: 8,
  paddingRight: 5,
  transform: "translateX(-8px)", 
  scrollSnapType: "x proximity",
scrollSnapStop: "always",
overscrollBehaviorX: "contain",
contain: "layout paint size",
  WebkitOverflowScrolling: "touch",
  scrollbarWidth: "none",
  msOverflowStyle: "none",
  touchAction: "auto",
};

const seenPrefetch = new Set<string>();
const prefetchAsset = (url?: string) => {
  if (!url || seenPrefetch.has(url)) return;

  const img = new Image();
  img.decoding = "async";
  img.fetchPriority = "high";
  img.src = url;

  try {
    const link = document.createElement("link");
    link.rel = "prefetch";
    link.href = url;
    document.head.appendChild(link);
  } catch {}

  seenPrefetch.add(url);
};

const prefetchPoster = (url?: string) => {
  if (!url || seenPrefetch.has(url)) return;

  const img = new Image();
  img.src = url;
  img.decoding = "async";
  img.fetchPriority = "high";

  // Stronger than prefetch — ensures instant availability
  const link = document.createElement("link");
  link.rel = "preload";
  link.as = "image";
  link.href = url;
  document.head.appendChild(link);

  seenPrefetch.add(url);
};


/** ——— Hook: useMediaOrientation ———
 * Determines if a media URL (image/video) is landscape. For videos, reads metadata.
 */
export function useMediaOrientation(
  url: string,
  isVideo: boolean,
  onResult: (isLandscape: boolean | null) => void
) {
  React.useEffect(() => {
    let alive = true;

    if (!url) {
      onResult(null);
      return;
    }

    if (isVideo) {
      const v = document.createElement("video");
      v.preload = "metadata";
      v.src = url;

      const handle = () => {
        if (!alive) return;
        const w = v.videoWidth || 0;
        const h = v.videoHeight || 0;
        onResult(w && h ? w > h : null);
        cleanup();
      };
      const err = () => {
        if (alive) onResult(null);
        cleanup();
      };
      const cleanup = () => {
        v.removeEventListener("loadedmetadata", handle);
        v.removeEventListener("error", err);
      };

      v.addEventListener("loadedmetadata", handle);
      v.addEventListener("error", err);
      try {
        v.load();
      } catch {}

      return () => {
        alive = false;
        cleanup();
      };
    } else {
      onResult(null);
    }
  }, [url, isVideo, onResult]);
}

/** ——— Component: ImageAutoFit ———
 * Loads the image to detect orientation, then applies portrait/landscape styles.
 */
export function ImageAutoFit({
  src,
  alt,
  portraitStyle,
  landscapeStyle,
  onOrientationChange,
  onClick,
  clickable,
}: {
  src: string;
  alt?: string;
  portraitStyle: React.CSSProperties;
  landscapeStyle: React.CSSProperties;
  onOrientationChange?: (isLandscape: boolean | null) => void;
  onClick?: () => void;
  clickable?: boolean;
}) {
  const [isLandscape, setIsLandscape] = React.useState<boolean | null>(null);
  const { t } = useTranslation();

  React.useEffect(() => {
    let alive = true;
    const img = new Image();
    img.onload = () => {
      if (!alive) return;
      const w = img.naturalWidth || 0;
      const h = img.naturalHeight || 0;
      const val = w && h ? w > h : null;
      setIsLandscape(val);
      onOrientationChange?.(val ?? null);
    };
    img.onerror = () => {
      if (alive) {
        setIsLandscape(null);
        onOrientationChange?.(null);
      }
    };
    img.src = src;
    return () => {
      alive = false;
    };
  }, [src, onOrientationChange]);

  const style = isLandscape ? landscapeStyle : portraitStyle;

  return (
    <div style={style}>
      <LazyMedia
        type="img"
        src={src}
        alt={alt || t('gallery.media.previewAlt')}
        style={{
          ...style,
          cursor: clickable ? "pointer" : undefined,
        }}
        onClick={onClick}
      />
    </div>
  );
}

/** ——— Component: MediaItem ———
 * One tile inside a post’s horizontal gallery. Handles image vs video rendering,
 * and orientation styles for images.
 */
export function MediaItem({
  index,
  media,
  isActive,
  isPrepared,
  globalMuted,
  registerChild,
  onMusicClick,
}: {
  index: number;
  media: MediaEntry;
  globalMuted: boolean;
  isActive: boolean;
  isPrepared: boolean;
  registerChild: (el: HTMLDivElement | null, i: number) => void;
  onMusicClick?: (entry: MediaEntry) => void;
}) {
  const { t } = useTranslation();

 const isVideo = isVideoEntry(media);
const mediaUrl = media.url || "";
const videoSrc = isVideo ? resolveVideoSrc(media) : "";

  const musicLabel = [media.musicTitle, media.musicArtist]
    .filter(Boolean)
    .join(" — ");


  useEffect(() => {
    if (media.posterUrl) {
      prefetchPoster(media.posterUrl);
    }
  }, [media.posterUrl]);

  return (
    <div
      ref={(el) => registerChild(el, index)}
      style={{
        position: "relative",
        height: MEDIA_HEIGHT,
        width: "100vw",
        maxWidth: "100vw",
        flex: "0 0 100vw",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        scrollSnapAlign: "start",
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      {/* COLUMN LAYOUT */}
      <div
        style={{
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
        }}
      >
        {/* VIDEO */}
        {isVideo && (
          <>
            <div
              style={{
                width: "100%",
                flex: "1 1 auto",        // 👈 video takes remaining height
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
              }}
            >
              {isPrepared && (
                <HLSPlayer
                  src={videoSrc}
                  poster={media.posterUrl || undefined}
                  muted={globalMuted}
                  active={isActive}
                  style={{
                    height: "100%",
                    width: "100%",
                    objectFit: "contain",
                    borderRadius: 12,
                  }}
                />
              )}
            </div>

            {/* MUSIC BAR UNDER VIDEO */}
            {(media.musicTitle || media.musicArtist) && (
              <div
                onClick={() => onMusicClick?.(media)}
                title={musicLabel}
                style={{
                  height: 26,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#7a7676",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  padding: "0 8px",
                  maxWidth: "92%",
                  marginTop: 4,
                }}
              >
                🎵 {musicLabel}
              </div>
            )}
          </>
        )}

        {/* IMAGE */}
        {!isVideo && mediaUrl && (
          <ImageAutoFit
            src={mediaUrl}
           alt={t('gallery.media.previewAlt')}
            portraitStyle={{
              height: MEDIA_HEIGHT,
              width: "100vw",
              maxWidth: "100vw",
              flex: "0 0 100vw",
              objectFit: "contain",
              borderRadius: 12,
            }}
            landscapeStyle={{
              height: MEDIA_HEIGHT,
              width: "100vw",
              maxWidth: "100vw",
              flex: "0 0 100vw",
              objectFit: "contain",
              borderRadius: 12,
            }}
            clickable
          />
        )}
      </div>
    </div>
  );
}

/** ——— Component: PostSection ———
 * Complete vertical page section for a single post (header, horizontal media strip, footer).
 */
export function PostSection({
  post,
  isActive,
  inWindow,
  globalMuted,
  navigate,
  onLike,
  onUnlike,
  currentUserId,
  onMusicClick,
  onAdClick,
}: {
  post: Post;
  isActive: boolean;
  inWindow: boolean;
  globalMuted: boolean;
  navigate: ReturnType<typeof useNavigate>;
  onLike: (id: string) => void;
  onUnlike: (id: string) => void;
  currentUserId?: string;
  onMusicClick?: (entry: MediaEntry) => void;
  onAdClick?: () => void; 
}) {
  const { t } = useTranslation();
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const itemRefs = React.useRef<HTMLDivElement[]>([]);
  const [currentIdx, setCurrentIdx] = React.useState(0);

  const [menuOpen, setMenuOpen] = useState(false);
const [reportOpen, setReportOpen] = useState(false);
const [selectedReason, setSelectedReason] = useState<string | null>(null);
const [reportStep, setReportStep] = useState<"list" | "details" | "done">("list");
const [details, setDetails] = useState("");


const handleReportPost = () => {
  setMenuOpen(false);
  setReportOpen(true);
  setReportStep("list");
};

const submitReport = async () => {
  if (!selectedReason) return;

  try {
    await api.post("/block-report/report/post", {
      postId: post._id,
      reasonKey: selectedReason,
      details: details || undefined,
    });

    setReportStep("done");

    setTimeout(() => {
      setReportOpen(false);
      setSelectedReason(null);
      setDetails("");
    }, 1200);

  } catch (err) {
    console.error(err);
    alert(t('gallery.report.failed'));
  }
};

//Report Button
const menuRef = useRef<HTMLDivElement | null>(null);

useEffect(() => {
  const handleClickOutside = (e: MouseEvent | TouchEvent) => {
    if (!menuRef.current) return;
    if (!menuRef.current.contains(e.target as Node)) {
      setMenuOpen(false);
    }
  };

  if (menuOpen) {
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
  }

  return () => {
    document.removeEventListener("mousedown", handleClickOutside);
    document.removeEventListener("touchstart", handleClickOutside);
  };
}, [menuOpen]);

 
  const isAdPost = !!post.isAd || (post as any).type === 'ad';
  const ctaText = post.adCtaText || t('gallery.ad.learnMore');
  const adUrl = post.adUrl;
 
  const nextMedia = post.media[currentIdx + 1];
  const nextVideoId = nextMedia ? resolvePrefetchVideoId(nextMedia) : null;


 useEffect(() => {
  if (!isActive) return;

  const container = containerRef.current;
  if (!container) return;

  // HARD reset
  container.style.scrollBehavior = "auto";
  container.scrollLeft = 0;

  // Lock again after layout settles
  requestAnimationFrame(() => {
    container.scrollLeft = 0;
  });

  setCurrentIdx(0);
}, [isActive]);



  useEffect(() => {
  const next = post.media[currentIdx + 1];
  const prev = post.media[currentIdx - 1];

  if (next?.url && !isVideoEntry(next)) prefetchAsset(next.url);
  if (prev?.url && !isVideoEntry(prev)) prefetchAsset(prev.url);

  if (next?.posterUrl) prefetchPoster(next.posterUrl);
  if (prev?.posterUrl) prefetchPoster(prev.posterUrl);

}, [currentIdx, post.media]);



  const ownerId = post.user?._id;
  const isOwnPost = !!ownerId && currentUserId === ownerId;
  const goToAuthor = () => {
    if (!ownerId) return;
    navigate(isOwnPost ? "/profile" : `/visitor-profile/${ownerId}`);
  };

  const goChatWithAuthor = () => {
  if (!post.user?._id || isOwnPost) return;

  navigate("/chat", {
    state: {
      toUserId: post.user._id,
      toUserName: post.user.profilename || "",
      toUserPicture: post.user.picture || "",
    },
  });

  setMenuOpen(false);
};

const openPostViewer = (post: Post, startIndex = 0) => {
  navigate('/openviewer', {
    state: {
      media: post.media,
      startIndex,
    },
  });
};

  const registerChild = (el: HTMLDivElement | null, i: number) => {
    if (el) itemRefs.current[i] = el;
  };

  React.useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const io = new IntersectionObserver(
      (entries) => {
        let best: { idx: number; ratio: number } | null = null;
        for (const e of entries) {
          const idx = itemRefs.current.findIndex((n) => n === e.target);
          if (idx >= 0) {
            const r = e.intersectionRatio;
            if (!best || r > best.ratio) best = { idx, ratio: r };
          }
        }
        if (best && best.ratio > 0) {
          if (Math.abs(root.scrollLeft - best!.idx * window.innerWidth) < 5) {
  setCurrentIdx(best!.idx);
}

        }
      },
      { root, threshold: [0.65]}
    );

    itemRefs.current.forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, [post._id]);

 const countryCode = post.location?.countryCode?.toUpperCase();
const translatedCountry = countryCode
  ? t(`countries.${countryCode}`, {
      defaultValue: post.location?.country || countryCode,
    })
  : post.location?.country || "";

const locationLabel =
  post.location?.city && translatedCountry
    ? `${post.location.city}, ${translatedCountry}`
    : translatedCountry
    ? translatedCountry
    : "";

  const dateLabel = t("gallery.post.postedAt", {
    date: new Date(post.createdAt).toLocaleString(),
  });

  const handleCtaClick = () => {
    if (!adUrl) return;
    window.open(adUrl, "_blank", "noopener,noreferrer");
  };



useHlsPrefetch(inWindow ? nextVideoId : null);


  return (
    <section
      data-post-id={post._id}
      style={{
        ...postCardStyle,
        minHeight: "100dvh",
        height: "auto",
        boxSizing: "border-box",
        scrollSnapAlign: "start",
        scrollSnapStop: "always",
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      {/* HEADER */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          justifyContent: "space-between",
          flexWrap: "wrap",
          marginLeft: 25,
          marginBottom: 3,
          marginTop: 16,
        }}
      >
        {/* LEFT: avatar + name + follow (same for ads + organic) */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            minWidth: 0,
          }}
        >
          <div
            onClick={goToAuthor}
            style={{
              width: 35,
              height: 35,
              borderRadius: "5%",
              backgroundColor: post.user?.picture
                ? "transparent"
                : "rgb(38, 29, 162)",
              backgroundImage: post.user?.picture
                ? `url(${post.user.picture})`
                : "none",
              backgroundSize: "cover",
              backgroundPosition: "center",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: post.user?.picture ? 0 : "20px",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            {!post.user?.picture && "👻"}
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              minWidth: 0,
            }}
          >
            <span
              onClick={goToAuthor}
              style={{
                cursor: "pointer",
                color: "#bbb",
                fontWeight: 600,
                fontSize: 15,
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                overflow: "hidden",
                maxWidth: 220,
              }}
            >
              {post.user?.profilename || t("gallery.post.unknownUser")}
            </span>


             <span style={{ display: "inline-flex", flexShrink: 0 }}>

     {!isOwnPost && !isAdPost && (
              <span style={{ display: "inline-flex", flexShrink: 0, gap: 6 }}>
  <FollowButtonInline
    authorId={post.user?._id}
    profilename={post.user?.profilename}
  />

  {/* 3-dot menu */}

  <div
  ref={menuRef}
  style={{ position: "relative" }}
  onClick={(e) => e.stopPropagation()}
>
  <button
    onClick={(e) => {
      e.stopPropagation();
      setMenuOpen((v) => !v);
    }}
    style={{
      background: "transparent",
      border: "none",
      color: "#bbb",
      fontSize: 16,
      cursor: "pointer",
      padding: "2px 6px",
    }}
  >
    ⋯
  </button>

  {menuOpen && (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        position: "absolute",
        left: "100%",
        top: "50%",
        transform: "translateY(-50%)",
        marginLeft: 6,
        background: "#111",
        border: "1px solid #333",
        borderRadius: 6,
        padding: "3px 5px",
        zIndex: 50,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        whiteSpace: "nowrap",
        boxShadow: "0 4px 12px rgba(0,0,0,0.35)",
      }}
    >

      <button
  onClick={(e) => {
    e.stopPropagation();
    e.preventDefault();
    goChatWithAuthor();
  }}
  style={{
    backgroundColor: "#111",
    color: "#bbb",
    border: "1px solid #D4AF37",
    borderRadius: "6px",
    padding: "3px 12px",
    minWidth: "85px",
    textAlign: "center",
    fontSize: "12px",
    cursor: "pointer",
  }}
>
  💬 {t("MyLikes.buttons.chat")}
</button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          handleReportPost();
        }}
        style={{
          padding: "3px 6px",
          textAlign: "left",
          background: "none",
          border: "none",
          color: "#ff4d4d",
          cursor: "pointer",
          fontSize: 11,
          lineHeight: 1.1,
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          whiteSpace: "nowrap",
          borderRadius: 4,
        }}
      >
        🚩 {t('gallery.report.button')}
      </button>
    </div>
  )}
</div>

</span>
)}
            </span>
          </div>
        </div>

        {/* RIGHT: differs for ad vs normal post */}
        {!isAdPost ? (
          // 🌍 Normal post: location + date + buttons (unchanged)
          <div
            style={{
              marginLeft: "auto",
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
              justifyContent: "flex-end",
              fontSize: 9,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                color: "#555",
                whiteSpace: "nowrap",
              }}
            >
              {locationLabel && (
  <button
    onClick={() => {
      const city = post.location?.city?.trim();
      const country = post.location?.country?.trim();
      const countryCode = post.location?.countryCode?.trim();

      if (!city || !(countryCode || country)) return;

      navigate(
        `/location-collection?city=${encodeURIComponent(city)}&country=${encodeURIComponent(
          country || ""
        )}&countryCode=${encodeURIComponent(countryCode || "")}`
      );
    }}
    style={{
      background: "transparent",
      border: "none",
      padding: 0,
      margin: 0,
      color: "#555",
      cursor: "pointer",
      fontSize: 13,
      whiteSpace: "nowrap",
    }}
    title={locationLabel}
  >
    📍 {locationLabel}
  </button>
)}
              <span>{dateLabel}</span>
            </div>

<div
  style={{
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexShrink: 0,
    minWidth: 0,
    marginRight: 10,
    marginTop: -2,

  }}
>
  {/* 🎬 Presentation Button */}
  <button
    onClick={() => openPostViewer(post, currentIdx)}
    disabled={!post.media || post.media.length === 0}
    title={t('myPosts.meta.openViewer')}
    style={{
      backgroundColor: "#000",
      color: "#bbb",
      border: "1px solid #333",
      borderRadius: 6,
      padding: "3px 8px",
      fontSize: 12,
      cursor: post.media?.length ? "pointer" : "not-allowed",
      opacity: post.media?.length ? 1 : 0.5,
      flexShrink: 0,
    }}
  >
    🎬 {t('myPosts.meta.presentation')}
  </button>

  {/* ❤️ Like button */}
  <button
    onClick={() =>
      post.likedByUser ? onUnlike(post._id) : onLike(post._id)
    }
    style={{
      backgroundColor: post.likedByUser ? "#ffccd5" : "#111",
      color: post.likedByUser ? "#a00" : "#bbb",
      border: "1px solid #ccc",
      borderRadius: "6px",
      padding: "3px 5px",
      fontSize: "12px",
      cursor: "pointer",
      transition: "all 0.3s ease",
      flexShrink: 0,
    }}
  >
    {post.likedByUser
      ? `💔 Unlike (${post.likes})`
      : `❤️ Like (${post.likes})`}
  </button>
</div>


          </div>
        ) : (
          // 📢 AD post: CTA button (with link) instead of location/date
          <div
            style={{
              marginLeft: "auto",
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
              marginTop: "35px",
              justifyContent: "flex-end",
              fontSize: 13,
            }}
          >
            {/* Optional small "Sponsored" label */}
            <span
              style={{
                fontSize: 12,
                color: "#a5b4fc",
                whiteSpace: "nowrap",
              }}
            >
              {post.adLabel || t('gallery.ad.sponsored')}
            </span>

            {/* CTA button with link */}
            {adUrl && (
              <button
  type="button"
  onClick={(e) => {
    e.stopPropagation();  // 👈 prevent swipe/scroll conflicts
    onAdClick?.();
    handleCtaClick();
  }}

                style={{
                  padding: "7px 16px",
                  borderRadius: 999,
                  border: "none",
                  cursor: "pointer",
                  background:
                    "linear-gradient(135deg, #3b82f6, #60a5fa)", // blue, as requested
                  color: "#f9fafb",
                  fontSize: 13,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                {ctaText}
              </button>
            )}
          </div>
        )}
      </div>

      {/* MEDIA AREA */}
 {inWindow ? (
<div style={{ display: "flex", flexDirection: "column" }}>
    <div
      ref={containerRef}
      style={{
        ...mediaScrollContainerStyle,
        pointerEvents: "auto",
      }}
    >
      {post.media.map((m, index) => (
        <MediaItem
          key={post._id + "-" + index}
          index={index}
          media={m}
          isActive={isActive && index === currentIdx}
          isPrepared={
          index === currentIdx ||
          index === currentIdx + 1 ||
          index === currentIdx - 1
}

          globalMuted={globalMuted}
          registerChild={registerChild}
          onMusicClick={onMusicClick}
        />
      ))}
    </div>

  </div>
) : (

        <div
          style={{
            height: MEDIA_HEIGHT,
            borderRadius: 12,
            background: "#050505",
          }}
        />
      )}

      <Modal
  open={reportOpen}
  onClose={() => {
    setReportOpen(false);
    setSelectedReason(null);
    setDetails("");
    setReportStep("list");
  }}
  title={t('gallery.report.title')}
  width={380}
>
  {/* STEP 1 */}
  {reportStep === "list" && (
    <>
      {[
  { key: "spam", label: t('gallery.report.reasons.spam') },
  { key: "hate", label: t('gallery.report.reasons.hate') },
  { key: "harassment", label: t('gallery.report.reasons.harassment') },
  { key: "sexual content", label: t('gallery.report.reasons.sexualContent') },
  { key: "scam or fraud", label: t('gallery.report.reasons.scamOrFraud') },
  { key: "violence", label: t('gallery.report.reasons.violence') },
  { key: "self-harm", label: t('gallery.report.reasons.selfHarm') },
  { key: "other", label: t('gallery.report.reasons.other') },
].map((r) => (
        <button
          key={r.key}
          onClick={() => {
            setSelectedReason(r.key);
            setReportStep("details");
          }}
          style={{
            width: "100%",
            padding: "8px 10px",
            background: "transparent",
            border: "none",
            borderBottom: "1px solid #222",
            color: "#ddd",
            textAlign: "left",
            cursor: "pointer",
            fontSize: 13,
          }}
        >
          {r.label}
        </button>
      ))}
    </>
  )}

  {/* STEP 2 */}
  {reportStep === "details" && (
    <>
      <textarea
        value={details}
        onChange={(e) => setDetails(e.target.value)}
        placeholder={t('gallery.report.detailsPlaceholder')}
        style={{
          width: "100%",
          minHeight: 70,
          borderRadius: 6,
          padding: 8,
          background: "#000",
          border: "1px solid #333",
          color: "#fff",
          marginBottom: 10,
          fontSize: 13,
        }}
      />

      <button
        onClick={submitReport}
        style={{
          width: "100%",
          padding: "8px",
          background: "#ff4d4d",
          border: "none",
          borderRadius: 6,
          color: "#fff",
          cursor: "pointer",
          fontSize: 13,
        }}
      >
        {t('gallery.report.submit')}
      </button>
    </>
  )}

  {/* STEP 3 */}
  {reportStep === "done" && (
    <div style={{ textAlign: "center", fontSize: 14 }}>
      ✅ {t('gallery.report.submitted')}
    </div>
  )}
</Modal>
    </section>
  );
}

export default {
  useMediaOrientation,
  ImageAutoFit,
  MediaItem,
  PostSection,
};
